import { CustomEventType } from "@/events/CustomEventRegistry";
import { IEventDataSource } from "@/types/interfaces";
import { BigBashEvent, Game, GamePoint, PointCalculationEnum, RoundPoint, Team } from "@/types/types";
import { getRoundPointFieldValue } from "@/utils/roundPointsFieldAccess";
import SmallHelpers from "@/utils/SmallHelpers";
import { sharedStyles } from "@lib/sharedStyles";
import { LitElement, PropertyValues, css, html } from "lit";
import { customElement, property, state } from "lit/decorators.js";
import { getFocusedTeamId, getTeamIdWithGamePointUpdate } from "./gamePointsMergeHandler";
import PointsCalculatorInputChangeHandler from "./PointsCalculatorInputChangeHandler";
import { QueueAndRunRequests, runRequestWithoutQueueing } from "@infrastructure/queueAndRunRequests";
import { UpdateRoundPointValue } from "./types/updateRoundPointValue";
import { UpdateTargetValue } from "./types/updateTargetValue";

const SINGLE_ROUND_GAME_NAMES = [
	// Skills
	'tetris',
	'domino',

	// Brain
	'sortieren - weniger/mehr (div. kategorien)',
	'wo ist walter?',
	'blamieren oder kassieren',
	'quiz (klassisch)',
	'jeopardy',
	'millionaire',

	// Action
	'blazepods',
	'multiball - hexagon',
	'multiball - tic tac toe',
	'multiball - bombs',
	'multiball - reaction',
];

type CalculatorDisplayConfig = {
	icon: string;
	title: string;
	columnLabel: string;
};

const CALCULATOR_DISPLAY_CONFIG: Record<PointCalculationEnum, CalculatorDisplayConfig> = {
	[PointCalculationEnum.mostPoints]: { icon: '🎯', title: 'Punkte Rechner (Meiste Punkte gewinnen)', columnLabel: 'Punkte pro Runde' },
	[PointCalculationEnum.fastestTime]: { icon: '⏱️', title: 'Zeit Rechner (Schnellste Zeit gewinnt)', columnLabel: 'Zeit pro Runde (Sekunden)' },
	[PointCalculationEnum.smallestDifference]: { icon: '📏', title: 'Differenz Rechner (Kleinste Abweichung gewinnt)', columnLabel: 'Schätzung pro Runde' },
};

const SYNC_ERROR_THRESHOLD = 2;
const SYNC_ERROR_MESSAGE = 'Punkte können gerade nicht aktualisiert werden. Erneuter Versuch läuft…';
const ROW_HIGHLIGHT_DURATION_MS = 1_000;

@customElement("round-value-calculator")
export class RoundValueCalculator extends LitElement {
	static readonly styles = [
		sharedStyles,
		css`
			.events-table {
				display: flex;
				flex-direction: column;
				gap: 8px;
				padding: 8px;
			}
			.table-header {
				display: flex;
				padding: 8px;
				background: #f8f9fa;
				gap: 8px;
			}
			.table-body {
				display: flex;
				flex-direction: column;
				gap: 16px;
			}
			.table-cell {
				flex: 1;
			}
			.table-cell--small {
				flex: 0.5;
			}
			.top-container {
				display: flex;
				justify-content: space-between;
				align-items: center;
				padding: 0 8px;
			}
			.rounds-container {
				display: flex;
				align-items: center;
				justify-content: space-between;
				gap: 8px;
				padding: 0 8px;
			}
			.rounds-container__target-values {
				display: flex;
				align-items: center;
				gap: 8px;
			}
			.sync-status {
				display: flex;
				align-items: center;
				gap: 8px;
				padding: 0 8px;
				color: var(--color-pink);
			}
			.activity-status {
				padding: 0 8px;
				font-family: var(--font-text);
				font-size: 0.8rem;
				color: black;
			}
			.sync-status__spinner {
				flex-shrink: 0;
				width: 16px;
				height: 16px;
				border: var(--border-width) solid var(--color-sand);
				border-top-color: transparent;
				border-radius: 50%;
				animation: sync-status-spin 0.8s linear infinite;
			}
			@keyframes sync-status-spin {
				to { transform: rotate(360deg); }
			}
		`
	];

	@property({ type: Object }) eventDataSource!: IEventDataSource;
	@property({ type: Object }) event!: BigBashEvent;
	@property({ type: Object }) game!: Game;
	@property({ type: String }) pointCalculationType: PointCalculationEnum = PointCalculationEnum.mostPoints;
	@property({ type: Number }) rounds: number = 3;
	@property({ type: Number }) consecutiveFailureCount = 0;
	@property({ type: Boolean }) isPollInFlight = false;
	@property({ attribute: false }) onGamePointsUpdated?: (event: BigBashEvent) => void;
	@property({ attribute: false }) queueAndRunRequests: QueueAndRunRequests = runRequestWithoutQueueing;
	@property({ attribute: false }) updateRoundPointValue: UpdateRoundPointValue;
	@property({ attribute: false }) updateTargetValue: UpdateTargetValue;

	@state() private targetValues: Map<number, number> = new Map();
	@state() private recentlyUpdatedTeamIds: Set<string> = new Set();

	private smallHelpers: SmallHelpers = new SmallHelpers();
	private focusedTeamId: string | null = null;
	private pendingRoundSyncByKey: Map<string, Promise<void>> = new Map();
	private pendingTargetValueSyncByRound: Map<number, Promise<void>> = new Map();
	private suppressFocusGuardForNextUpdate = false;
	private lastTrustedEvent!: BigBashEvent;

	constructor() {
		super();
		this.addEventListener(CustomEventType.INPUT_CHANGE, this._handleInputChange);
		this.addEventListener(CustomEventType.ROUND_POINT_CHANGE, this._handleValueChange);
		this.addEventListener(CustomEventType.FASTEST_TIME_CHANGE, this._handleValueChange);
		this.addEventListener(CustomEventType.SMALLEST_DIFFERENCE_CHANGE, this._handleValueChange);
		this.addEventListener('focusin', this._handleFocusIn);
		this.addEventListener('focusout', this._handleFocusOut);
	}

	private get inputChangeHandler(): PointsCalculatorInputChangeHandler {
		return new PointsCalculatorInputChangeHandler(this.pointCalculationType);
	}

	private _isSmallestDifference(): boolean {
		return this.pointCalculationType === PointCalculationEnum.smallestDifference;
	}

	private _isSingleRoundGame(game: Game): boolean {
		const gameName = game?.name?.toLowerCase() || '';
		return SINGLE_ROUND_GAME_NAMES.includes(gameName);
	}

	private _getDefaultRoundsForGame(game: Game): number {
		return this._isSingleRoundGame(game) ? 1 : 3;
	}

	connectedCallback(): void {
		super.connectedCallback();
		if (this._isSmallestDifference()) {
			this._syncTargetValuesFromEvent();
		}
		const defaultRounds = this._getDefaultRoundsForGame(this.game);
		const calculatedRounds = this.smallHelpers.calculateRoundsCount(this.event.game_points || [], this.game.id, defaultRounds);
		this.rounds = this._isSingleRoundGame(this.game) ? 1 : calculatedRounds;
	}

	protected willUpdate(changedProperties: PropertyValues): void {
		if (!changedProperties.has('event')) {
			return;
		}
		const previousEvent = changedProperties.get('event') as BigBashEvent | undefined;
		if (!previousEvent) {
			this.lastTrustedEvent = this.event;
			return;
		}

		if (this.suppressFocusGuardForNextUpdate) {
			this.suppressFocusGuardForNextUpdate = false;
			return;
		}

		const teamIdsWithUnconfirmedLocalEdits = this._getTeamIdsWithPendingRoundSync();
		if (this.focusedTeamId) {
			teamIdsWithUnconfirmedLocalEdits.add(this.focusedTeamId);
		}
		teamIdsWithUnconfirmedLocalEdits.forEach(teamId => {
			this.event = this._keepTrustedTeamValue(teamId);
		});

		Array.from(this.pendingTargetValueSyncByRound.keys()).forEach(roundNumber => {
			this.event = this._keepTrustedTargetValue(roundNumber);
		});
		this.lastTrustedEvent = this.event;

		if (this._isSmallestDifference()) {
			this._syncTargetValuesFromEvent();
		}

		const changedTeamIds = getTeamIdWithGamePointUpdate(previousEvent.game_points, this.event.game_points, this.game.id)
			.filter(teamId => !teamIdsWithUnconfirmedLocalEdits.has(teamId));
		this._highlightTeamRows(changedTeamIds);
	}

	private _getTeamIdsWithPendingRoundSync(): Set<string> {
		return new Set(Array.from(this.pendingRoundSyncByKey.keys(), syncKey => syncKey.split(':')[1]));
	}

	private _keepTrustedTeamValue(teamId: string): BigBashEvent {
		const trustedGamePoint = this.lastTrustedEvent.game_points.find(
			gamePoint => gamePoint.team === teamId && gamePoint.game === this.game.id
		);
		if (!trustedGamePoint) {
			return this.event;
		}
		const hasExistingEntry = this.event.game_points.some(
			gamePoint => gamePoint.team === teamId && gamePoint.game === this.game.id
		);
		const game_points = hasExistingEntry
			? this.event.game_points.map(gamePoint =>
				(gamePoint.team === teamId && gamePoint.game === this.game.id) ? trustedGamePoint : gamePoint
			)
			: [...this.event.game_points, trustedGamePoint];
		return { ...this.event, game_points };
	}

	private _keepTrustedTargetValue(roundNumber: number): BigBashEvent {
		const game_points = this.event.game_points.map(gamePoint => {
			if (gamePoint.game !== this.game.id) {
				return gamePoint;
			}
			const trustedGamePoint = this.lastTrustedEvent.game_points.find(
				trusted => trusted.team === gamePoint.team && trusted.game === this.game.id
			);
			const trustedRoundPoint = trustedGamePoint?.rounds.find(roundPoint => roundPoint.round === roundNumber);
			if (!trustedRoundPoint) {
				return gamePoint;
			}
			return {
				...gamePoint,
				rounds: gamePoint.rounds.map(roundPoint =>
					roundPoint.round === roundNumber
						? { ...roundPoint, round_target_value: trustedRoundPoint.round_target_value }
						: roundPoint
				)
			};
		});
		return { ...this.event, game_points };
	}

	private _setOptimisticLocalEvent(event: BigBashEvent): void {
		this.suppressFocusGuardForNextUpdate = true;
		this.lastTrustedEvent = event;
		this.event = event;
	}

	private _highlightTeamRows(teamIds: string[]): void {
		if (teamIds.length === 0) {
			return;
		}
		this.recentlyUpdatedTeamIds = new Set([...Array.from(this.recentlyUpdatedTeamIds), ...teamIds]);
		setTimeout((): void => {
			const stillHighlighted = new Set(this.recentlyUpdatedTeamIds);
			teamIds.forEach(teamId => stillHighlighted.delete(teamId));
			this.recentlyUpdatedTeamIds = stillHighlighted;
		}, ROW_HIGHLIGHT_DURATION_MS);
	}

	private _handleFocusIn = (event: FocusEvent): void => {
		this.focusedTeamId = getFocusedTeamId(event.composedPath());
	};

	private _handleFocusOut = (): void => {
		this.focusedTeamId = null;
	};

	private _syncTargetValuesFromEvent(): void {
		const gamePoints = this.event.game_points?.filter(gamePoint => gamePoint.game === this.game.id);
		if (!gamePoints?.length) return;
		const firstGamePoint = gamePoints[0];
		firstGamePoint.rounds.forEach(roundPoint => {
			if (roundPoint.round_target_value != null) {
				this.targetValues = new Map(this.targetValues).set(roundPoint.round, roundPoint.round_target_value);
			}
		});
	}

	private _sortTeamsByNumber(teams: Team[]): Team[] {
		return teams.slice().sort((a, b) => a.team_number - b.team_number);
	}

	private _filterGamePointsByTeamAndGameID(teamId: string, gameId: string): GamePoint | undefined {
		return this.event.game_points?.find(gamePoint => gamePoint.team === teamId && gamePoint.game === gameId);
	}

	private _getDisabledRounds(): number[] {
		if (!this._isSmallestDifference()) {
			return [];
		}
		const disabledRounds: number[] = [];
		for (let i = 0; i < this.rounds; i++) {
			const targetValue = this.targetValues.get(i);
			if (!targetValue || targetValue === 0) {
				disabledRounds.push(i);
			}
		}
		return disabledRounds;
	}

	private _updateTargetValueInEvent(roundIndex: number, targetValue: number): Array<GamePoint> {
		let gamePoints = [...(this.event.game_points || [])];

		for (const team of this.event.teams) {
			const existingGamePoint = gamePoints.find(
				gamePoint => gamePoint.team === team.id && gamePoint.game === this.game.id
			);

			if (!existingGamePoint) {
				const newGamePoint: GamePoint = {
					id: crypto.randomUUID(),
					game: this.game.id,
					team: team.id,
					rounds: [],
					created_at: new Date(),
					updated_at: new Date(),
				};
				gamePoints.push(newGamePoint);
			}
		}

		return gamePoints.map(gamePoint => {
			if (gamePoint.game !== this.game.id) return gamePoint;

			const existingRoundPoint = gamePoint.rounds.find(roundPoint => roundPoint.round === roundIndex);

			if (existingRoundPoint) {
				return {
					...gamePoint,
					rounds: gamePoint.rounds.map(roundPoint =>
						roundPoint.round === roundIndex
							? { ...roundPoint, round_target_value: targetValue, updated_at: new Date() }
							: roundPoint
					)
				};
			}

			const newRoundPoint: RoundPoint = {
				id: crypto.randomUUID(),
				round: roundIndex,
				points: null,
				team_fastest_time_seconds: null,
				team_smallest_difference_value: null,
				round_target_value: targetValue,
				created_at: new Date(),
				updated_at: new Date(),
			};
			return {
				...gamePoint,
				rounds: [...gamePoint.rounds, newRoundPoint]
			};
		});
	}

	private _handleInputChange = (event: CustomEvent): void => {
		const { additionalIdentifier, name, value } = event.detail;

		if (additionalIdentifier === "increase-decrease-number") {
			this.rounds = this._isSingleRoundGame(this.game) ? 1 : (parseInt(value, 10) || 0);
			this.requestUpdate();
			return;
		}

		if (this._isSmallestDifference() && additionalIdentifier?.startsWith("target-value-round-")) {
			const roundMatch = name.match(/^round-(\d+)$/);
			if (roundMatch) {
				const roundIndex = parseInt(roundMatch[1], 10);
				const numericValue = parseFloat(value) || 0;

				this.targetValues = new Map(this.targetValues).set(roundIndex, numericValue);
				this._setOptimisticLocalEvent({ ...this.event, game_points: this._updateTargetValueInEvent(roundIndex, numericValue) });

				const saveThisChange = (): Promise<void> =>
					this.updateTargetValue(this.game.id, roundIndex, numericValue).then(() => undefined);

				const previousTargetValueSync = this.pendingTargetValueSyncByRound.get(roundIndex);
				const thisTargetValueSync: Promise<void> = previousTargetValueSync
					? previousTargetValueSync.catch(() => undefined).then(saveThisChange)
					: saveThisChange();

				this.pendingTargetValueSyncByRound.set(roundIndex, thisTargetValueSync);
				thisTargetValueSync.finally(() => {
					if (this.pendingTargetValueSyncByRound.get(roundIndex) === thisTargetValueSync) {
						this.pendingTargetValueSyncByRound.delete(roundIndex);
					}
				});
			}
		}
	};

	private _getRoundSyncKey(gameId: string, teamId: string, roundNumber: number): string {
		return `${gameId}:${teamId}:${roundNumber}`;
	}

	private _handleValueChange = (event: CustomEvent): Promise<void> => {
		const { gameId, teamId, name, value } = event.detail;
		const roundNumber = this.inputChangeHandler.getRoundNumberFromRoundName(name);
		const syncKey = this._getRoundSyncKey(gameId, teamId, roundNumber);

		this._setOptimisticLocalEvent(this.inputChangeHandler.updateEvent(this.event, gameId, teamId, name, value));
		this.requestUpdate();

		const field = this.inputChangeHandler.getFieldForCalculationType();
		const updatedRoundPoint = this.inputChangeHandler.findExistingRoundPoint(this.event, gameId, teamId, roundNumber);
		const numericValue = updatedRoundPoint ? getRoundPointFieldValue(updatedRoundPoint, field) : 0;

		const saveThisChange = (): Promise<void> =>
			this.updateRoundPointValue(gameId, teamId, roundNumber, field, numericValue).then(() => undefined);

		const previousRoundSync = this.pendingRoundSyncByKey.get(syncKey);
		const thisRoundSync: Promise<void> = previousRoundSync
			? previousRoundSync.catch(() => undefined).then(saveThisChange)
			: saveThisChange();

		this.pendingRoundSyncByKey.set(syncKey, thisRoundSync);
		thisRoundSync.finally(() => {
			if (this.pendingRoundSyncByKey.get(syncKey) === thisRoundSync) {
				this.pendingRoundSyncByKey.delete(syncKey);
			}
		});
		return thisRoundSync;
	};

	private _getActivityStatusText(): string | null {
		if (this.pendingRoundSyncByKey.size > 0) {
			return 'Speichere Punkte…';
		}
		if (this.pendingTargetValueSyncByRound.size > 0) {
			return 'Speichere Zielwert…';
		}
		if (this.isPollInFlight) {
			return 'Checke Punkte-Updates…';
		}
		return null;
	}

	render() {
		const isSingleRound = this._isSingleRoundGame(this.game);
		const disabledRounds = this._getDisabledRounds();
		const displayConfig = CALCULATOR_DISPLAY_CONFIG[this.pointCalculationType];
		const activityStatusText = this._getActivityStatusText();

		return html`
			<div class="round-value-calculator-container">
				<div class="top-container">
					<h2>${displayConfig.icon} ${displayConfig.title}</h2>
					${activityStatusText ? html`<span class="activity-status" role="status" aria-live="polite">${activityStatusText}</span>` : ''}
				</div>
				${this.consecutiveFailureCount >= SYNC_ERROR_THRESHOLD ? html`
					<div class="sync-status" role="status" aria-live="polite">
						<span class="sync-status__spinner"></span>
						<span>${SYNC_ERROR_MESSAGE}</span>
					</div>
				` : ''}
				<div class="rounds-container">
					${this._isSmallestDifference() ? html`
						<div class="rounds-container__target-values">
							<label for="target-value">Zielwert pro Runde:</label>
							${Array.from({ length: this.rounds }, (_, i) => html`
								<basic-number-input
									style="width: 80px;"
									.inputEventIdentifier=${`target-value-round-${i}`}
									.name=${`round-${i}`}
									.value=${String(this.targetValues.get(i) || 0)}
								></basic-number-input>
							`)}
						</div>
					` : ''}
					<div class="rounds-container__number-of-rounds">
						<label for="number-of-rounds">Anzahl Runden:</label>
						<increase-decrease-number
							.value=${this.rounds}
							.min=${isSingleRound ? 1 : 0}
							.max=${isSingleRound ? 1 : null}
							.disabled=${isSingleRound}
						></increase-decrease-number>
					</div>
				</div>
				<div class="events-table">
					<div class="table-header">
						<div class="table-cell--small text-body--small">Team Nr.</div>
						<div class="table-cell text-body--small">Team</div>
						<div class="table-cell text-body--small" style="flex: 2;">${displayConfig.columnLabel}</div>
					</div>
					<div class="table-body">
						${this._sortTeamsByNumber(this.event.teams).map((team) => html`
							<points-calculator-row
								.pointCalculationType=${this.pointCalculationType}
								.gamePoint="${this._filterGamePointsByTeamAndGameID(team.id, this.game.id)}"
								.rounds="${this.rounds}"
								.team="${team}"
								.gameId="${this.game.id}"
								.disabledRounds="${disabledRounds}"
								.highlight="${this.recentlyUpdatedTeamIds.has(team.id)}">
							</points-calculator-row>
						`)}
					</div>
				</div>
			</div>
		`;
	}
}

export default RoundValueCalculator;
